import React, { useEffect, useRef } from "react";
import {
    Heading,
    CodeBlock,
    ClassicEditor,
    Context,
    Bold,
    Essentials,
    Strikethrough,
    Italic,
    Subscript,
    Superscript,
    Underline,
    Paragraph,
    ContextWatchdog,
    BlockQuote,
    Font,
    FontSize,
    FontColor,
    AutoLink,
    Link,
    List,
    AdjacentListsSupport,
    Table,
    TableToolbar,
    TableProperties,
    TableCellProperties,
    TableColumnResize,
    Alignment,
    Undo,
    Indent,
    IndentBlock,
    ImageInsert,
    ImageUpload,
    AutoImage,
    Image,
    ImageToolbar,
    ImageCaption,
    ImageStyle,
    ImageResize,
    LinkImage,
    GeneralHtmlSupport,
    PasteFromMarkdownExperimental,
    Base64UploadAdapter
} from "ckeditor5";

import { CKEditor, CKEditorContext } from "@ckeditor/ckeditor5-react";
import "ckeditor5/ckeditor5.css";
import "./Editor.css"; // Import the CSS file

interface TextEditorProps {
    value: string;
    handleChange: (event: React.FormEvent, data: string) => void;
}

const TextEditor: React.FC<TextEditorProps> = ({ value, handleChange }) => {

    const editorRef = useRef<any>(null);

    useEffect(() => {
        if (editorRef.current) {
            const editor = editorRef.current;
            
            // Only update if the value is different from the editor's current data
            if (editor.getData() !== value) {
                editor.setData(value);
            }
        }
    }, [value]);



    const customColorPalette = [
        { color: "#FFC1BD", label: "Peach Pink" },
        { color: "#FFB9D0", label: "Soft Blush" },
        { color: "#F8CFFF", label: "Lavender Blush" },
        { color: "#D1B7FF", label: "Soft Lilac" },
        { color: "#C3CBFF", label: "Pale Periwinkle" },
        { color: "#ACDAFF", label: "Sky Blue" },
        { color: "#D4FFD0", label: "Mint Frost" },
        { color: "#FFF4C3", label: "Buttercup Glow" },
        { color: "#D1FFFD", label: "Frosted Aqua" },
        { color: "#031227", label: "Black" }
    ];


    return (
        <div style={{ fontFamily: "Manrope !important" }}>
                    {/* eslint-disable-next-line */}
            <CKEditorContext context={Context} contextWatchdog={ContextWatchdog}>
                
                <CKEditor
          
                    editor={ClassicEditor}
                    // data='please enter text'
                    
                    config={{
                        
                        plugins: [
                            Heading, CodeBlock, Essentials, Bold, Italic, 
                            Strikethrough, Subscript, Superscript,
                            Underline, Paragraph, BlockQuote, Font, Link, 
                            AutoLink, List, AdjacentListsSupport,
                            Table, TableToolbar, TableProperties, 
                            TableCellProperties, TableColumnResize, Alignment,
                            Undo, Indent, IndentBlock, FontSize, 
                            FontColor, ImageInsert, ImageUpload, AutoImage,
                            Image, ImageToolbar, ImageCaption, 
                            ImageStyle, ImageResize, LinkImage, GeneralHtmlSupport,
                            PasteFromMarkdownExperimental, Base64UploadAdapter
                        ],
                        toolbar: {
                            items: [
                                "heading", "bold", "italic", 
                                "underline", "strikethrough", 
                                "subscript", "superscript",
                                "|", "blockQuote", "fontSize", 
                                "fontColor", "fontBackgroundColor", 
                                "|", "image", "imageInsert",
                                "link", "insertTable", "|", 
                                "bulletedList", "numberedList", 
                                "undo", "redo", "|", "alignment", 
                                "outdent", "indent"
                            ]
                        },
                        heading: {
                            options: [
                                { model: "paragraph", 
                                    title: "Paragraph", 
                                    class: "ck-heading_paragraph" },
                                { model: "heading1", 
                                    view: "h1", 
                                    title: "Heading 1", 
                                    class: "ck-heading_heading1" },
                                { model: "heading2", 
                                    view: "h2", 
                                    title: "Heading 2", 
                                    class: "ck-heading_heading2" }
                            ]
                        },
                        codeBlock: {
                            languages: [
                                { language: "javascript", 
                                    label: "JavaScript", 
                                    class: "js-code" },
                                { language: "python", 
                                    label: "Python", 
                                    class: "python-code" }
                            ]
                        },
                        fontSize: { options: [8, 9, 10, 11, 12, 14, 16, 18, 24] },
                        fontFamily: { options: ["default", "Manrope"] },
                        image: { insert: { integrations: ["assetManager", "url", "upload"] } },
                        list: { properties: { styles: true, startIndex: true, reversed: true } },
                        table: {
                            contentToolbar: ["tableColumn", "tableRow", "mergeTableCells", "tableProperties", "tableCellProperties"],
                            tableProperties: { borderColors: customColorPalette,
                                 backgroundColors: customColorPalette,
                                  defaultProperties: { width: "550px", height: "450px" } },
                            tableCellProperties: { borderColors: customColorPalette,
                                 backgroundColors: customColorPalette },
                        },
                        
                    }}
           
                    onReady={(editor) => {
                        if (value!==''){
                            editor.setData(value); // Set initial value here
                            editorRef.current = editor;
                        }
                        
                    }}
                    onBlur={(event, editor) =>
                         handleChange(event as unknown as React.FormEvent, editor.getData())}
                    onChange={(event, editor) => 
                        handleChange(event as unknown as React.FormEvent, editor.getData())}
                />
            </CKEditorContext>
        </div>
    );
};

export default TextEditor;
